'use client';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import type { Options as SplideOptions } from '@splidejs/splide';
import '@splidejs/react-splide/css';
import { ChevronIcon } from '@/assets/icons/ChevronIcon';
import { responsiveBreakpointsPx } from '@/constants';
import styles from './contentcard.module.scss';
import cn from 'classnames';
import { ContentCardGroupProps, ContentSliderOptionsProps } from './ContentCard.type';

export function ContentCardGroupCarousel({ children, cardCount, theme }: ContentCardGroupProps): React.JSX.Element {
    const themeSliderOptions: { [themeName: string]: ContentSliderOptionsProps } = {
        horizontal: {
            fixedWidthXl: '39%',
            fixedWidthLg: '70%',
            fixedWidthMd: '70%',
            arrowsDesktop: cardCount > 4 ? true : false,
        },
        vertical: {
            fixedWidthXl: '22%',
            fixedWidthLg: '39%',
            fixedWidthMd: '70%',
            arrowsDesktop: cardCount > 4,
        },
        featuredcard: {
            // 917px stops scaling at ~1440px viewport width:
            fixedWidthXl: 'min(70%, 917px)',
            fixedWidthLg: 'min(70%, 917px)',
            fixedWidthMd: 'min(70%, 917px)',
            arrowsDesktop: cardCount > 1,
        },
    };

    const sliderOptions = getContentCardSliderOptions(themeSliderOptions[theme ?? 'vertical']);

    return (
        <Splide hasTrack={false} options={sliderOptions}>
            <SplideTrack className={styles.carouselTrack}>
                {children.map((child, idx) => (
                    <SplideSlide key={idx} className={styles.carouselSlide}>
                        {child}
                    </SplideSlide>
                ))}
            </SplideTrack>

            <div className={`splide__arrows ${styles.carouselArrows}`}>
                <button
                    className={cn('splide__arrow splide__arrow--prev', styles.leftArrow)}
                    aria-label={`navigate previous arrow`}
                >
                    <ChevronIcon />
                </button>
                <button
                    className={cn('splide__arrow splide__arrow--next', styles.rightArrow)}
                    aria-label={`navigate next arrow`}
                >
                    <ChevronIcon />
                </button>
            </div>
        </Splide>
    );
}

export function getContentCardSliderOptions({
    fixedWidthXl,
    fixedWidthLg,
    fixedWidthMd,
    arrowsDesktop = false,
}: ContentSliderOptionsProps): SplideOptions {
    return {
        rewind: false,
        focus: 0,
        omitEnd: true,
        gap: '16px',
        pagination: false,
        arrows: arrowsDesktop,
        drag: true,
        focusableNodes: 'button, textarea, input, select, iframe',
        fixedWidth: fixedWidthXl,
        perMove: 1,
        padding: 0,
        type: 'slide',
        breakpoints: {
            [responsiveBreakpointsPx.lg]: {
                gap: '16px',
                pagination: false,
                fixedWidth: fixedWidthLg,
                perMove: 1,
                arrows: false,
                drag: true,
                padding: 0,
                type: 'slide',
            },
            [responsiveBreakpointsPx.md]: {
                gap: '16px',
                pagination: false,
                perMove: 1,
                fixedWidth: fixedWidthMd,
                arrows: false,
                padding: {
                    left: '16px',
                    right: '24px',
                },
                drag: true,
                type: 'slide',
            },
        },
    };
}
